import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import {
  Island,
  PublicIsland,
  NewIsland as updatedIsland,
} from '../types/island';
import {
  ChangeOperatorStatusPayload,
  NewOperator,
  Operator,
} from '../types/operator';

import { PublicIslandJourney } from '../pages/public/types';
import { AnalyticsInfo } from '../types/analytics';
import {
  Contract,
  ContractAssignedVessels,
  ContractListItem,
  NewContract,
  SimpleContractAssignedVessels,
} from '../types/contract';
import { AppData, ErrorType, mapFileToAppFile } from '../types/core';
import {
  ApiCrewMemberCertificate,
  CrewMember,
  CrewMemberCertificate,
  CrewMemberListItem,
  NewCrewMember,
  NewCrewMemberCertificate,
  UpdatedCrewMember,
} from '../types/crew';
import {
  AdminDashboardInfo,
  AdminDashboarPendingInvoiceElement,
  OperatorDashboardInfo,
} from '../types/dashboard';
import {
  ApiContractInsurance,
  ApiVesselInsurance,
  ContractInsurance,
  NewContractInsurance,
  NewVesselInsurance,
  VesselInsurance,
} from '../types/insurance';
import {
  CreateInvoiceResponse,
  Invoice,
  InvoiceJourney,
  InvoiceListItem,
  InvoiceRejectionNote,
  InvoiceValidationErrors,
  NewInvoice,
} from '../types/invoices';
import {
  Journey,
  JourneyChangePayload,
  JourneyChangeResponse,
  JourneyLog,
  JourneyPlaceholder,
  NewJourney,
  UpdatedJourneyLog,
} from '../types/journey';
import { NewPort, Port, UpdatedPort } from '../types/port';
import { Route, SimpleNewRoute, SimpleRoute } from '../types/route';
import { NewUser, User, UserOperator } from '../types/user';
import {
  ApiVesselCertificate,
  NewVessel,
  NewVesselCertificate,
  UpdatedVessel,
  Vessel,
  VesselCertificate,
  VesselContractAssignment,
  VesselListItem,
} from '../types/vessel';
import { JourneyReasonNote } from '../pages/admin/Journeys/types';

export class IotsApiError {
  type: ErrorType;
  message: string;

  constructor(type: ErrorType, message: string) {
    this.type = type;
    this.message = message;
  }

  toString() {
    return this.message;
  }
}

interface ApiSuccessResponse<T = void> {
  isSuccess: true;
  data: T;
}

interface ApiErrorResponse {
  isSuccess: false;
  error: {
    message: string;
    type: ErrorType;
  };
}

type ApiResponse<T = void> = ApiSuccessResponse<T> | ApiErrorResponse;

const handleAxiosApiRequest = async <T>(
  request: Promise<AxiosResponse<ApiResponse<T>>>,
): Promise<T> => {
  const response = await request.catch(error => {
    if (error instanceof AxiosError) {
      throw new Error(error.message);
    }
    throw error;
  });
  if (!response.data.isSuccess) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw new IotsApiError(
      response.data.error.type,
      response.data.error.message,
    );
  }
  return response.data.data;
};

export const fetchAppData = (client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<AppData>>(`v1.0/configurations`),
  );

export const fetchContracts = (client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<ContractListItem[]>>(`v1.0/contracts`),
  );

export const fetchContractById = (contractId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<Contract>>(`v1.0/contracts/${contractId}`),
  );

export const createContract = (contract: NewContract, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.post<ApiResponse<Contract>>(`v1.0/contracts`, contract),
  ).then(newContract => newContract.id);

export const deleteContract = (contractId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.delete<ApiResponse>(`v1.0/contracts/${contractId}`),
  );

export const rescindContract = (contractId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.put<ApiResponse<Contract>>(`v1.0/contracts/${contractId}/rescind`),
  );

export const updateContract = (
  contract: NewContract & { id: string },
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.put<ApiResponse>(`v1.0/contracts/${contract.id}`, contract),
  );

export const fetchContractRoutes = (
  contractId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<Route[]>>(`v1.0/contracts/${contractId}/routes`),
  );

export const createContractRoute = (
  contractId: string,
  newRoute: SimpleNewRoute,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.post<ApiResponse<SimpleRoute>>(
      `v1.0/contracts/${contractId}/routes`,
      newRoute,
    ),
  );

export const fetchContractRouteDeletionImpactedJourneys = (
  contractId: string,
  routeId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<{ journeyNumber: number }>>(
      `v1.0/contracts/${contractId}/routes/${routeId}/deletevalidation`,
    ),
  ).then(data => data.journeyNumber);

export const deleteContractRoute = (
  contractId: string,
  routeId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.delete<ApiResponse>(
      `v1.0/contracts/${contractId}/routes/${routeId}`,
    ),
  );

export const rescindContractRoute = (
  contractId: string,
  routeId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.post<ApiResponse>(
      `v1.0/contracts/${contractId}/routes/${routeId}/rescind`,
    ),
  );

export const fetchContractAssignedVessels = (
  contractId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<ContractAssignedVessels | undefined>>(
      `v1.0/contracts/${contractId}/vessels`,
    ),
  ).then(data => data ?? null);

export const updateContractAssignedVessels = (
  contractId: string,
  newAssignedVessels: SimpleContractAssignedVessels,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.post<ApiResponse>(
      `v1.0/contracts/${contractId}/vessels`,
      newAssignedVessels,
    ),
  );

export const fetchUsers = (client: AxiosInstance) =>
  handleAxiosApiRequest(client.get<ApiResponse<User[]>>(`v1.0/users`));

export const createUser = (user: NewUser, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.post<ApiResponse<{ userId: string }>>(`v1.0/users`, user),
  ).then(({ userId }) => userId);

export const updateUser = (user: User, client: AxiosInstance) => {
  const { id, ...restOfUser } = user;
  return handleAxiosApiRequest(
    client.put<ApiResponse>(`v1.0/users/${id}`, restOfUser),
  );
};

export const updateUserState = (
  id: string,
  isActive: boolean,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.patch<ApiResponse>(`v1.0/users/${id}`, [
      { key: 'isActive', value: isActive.toString() },
    ]),
  );

export const fetchUserById = (client: AxiosInstance, userId: string) =>
  handleAxiosApiRequest(client.get<ApiResponse<User>>(`v1.0/users/${userId}`));

export const fetchUserOperator = (client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<UserOperator>>(`v1.0/users/operator`),
  );

export const resendEmailInvitation = (user: User, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse>(`v1.0/users/${user.id}/resend-email`),
  );

export const fetchOperators = (client: AxiosInstance) =>
  handleAxiosApiRequest(client.get<ApiResponse<Operator[]>>(`v1.0/operators`));

export const createOperator = async (
  operator: NewOperator,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.post<ApiResponse<{ id: string }>>(`v1.0/operators`, operator),
  ).then(({ id }) => id);

export const fetchOperatorById = (operatorId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<Operator>>(`v1.0/operators/${operatorId}`),
  );

export const updateOperator = (operator: Operator, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.put<ApiResponse>(`v1.0/operators/${operator.id}`, operator),
  );

export const updateOperatorState = (
  operator: ChangeOperatorStatusPayload,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.patch<ApiResponse>(`v1.0/operators/${operator.id}`, [
      { key: 'isActive', value: `${operator.isActive}` },
    ]),
  );

export const fetchPublicIslands = (client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<PublicIsland[]>>(`v1.0/publics/islands`),
  );

export const fetchPublicJourneysByIslandId = (
  islandId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<PublicIslandJourney[]>>(
      `v1.0/publics/journeys/${islandId}`,
    ),
  );

export const fetchIslands = (client: AxiosInstance) =>
  handleAxiosApiRequest(client.get<ApiResponse<Island[]>>(`v1.0/islands`));

export const fetchIslandById = (id: string, client: AxiosInstance) =>
  handleAxiosApiRequest(client.get<ApiResponse<Island>>(`v1.0/islands/${id}`));

export const createIsland = (island: updatedIsland, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.post<ApiResponse<{ islandId: string }>>(`v1.0/islands`, island),
  ).then(newIsland => newIsland.islandId);

export const updateIsland = (
  islandId: string,
  updatedIsland: updatedIsland,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.put<ApiResponse>(`v1.0/islands/${islandId}`, updatedIsland),
  );

export const updateIslandVisibility = (
  islandId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.patch<ApiResponse>(`v1.0/islands/${islandId}/updatevisibility`),
  );

export const fetchPorts = (client: AxiosInstance): Promise<Port[]> =>
  handleAxiosApiRequest(client.get<ApiResponse<Port[]>>(`v1.0/ports`));

export const createPort = (port: NewPort, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.post<ApiResponse<{ portId: string }>>(`v1.0/ports`, port),
  ).then(newPort => newPort.portId);

export const updatePort = (
  portId: string,
  updatedPort: UpdatedPort,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.put<ApiResponse>(`v1.0/ports/${portId}`, updatedPort),
  );

export const deletePort = (portId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(client.delete<ApiResponse>(`v1.0/ports/${portId}`));

export const fetchVessels = (
  client: AxiosInstance,
): Promise<VesselListItem[]> =>
  handleAxiosApiRequest(
    client.get<ApiResponse<VesselListItem[]>>(`v1.0/vessels`),
  );

export const fetchVesselById = (vesselId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<Vessel>>(`v1.0/vessels/${vesselId}`),
  );

export const createVessel = (vessel: NewVessel, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.post<ApiResponse<Vessel>>(`v1.0/vessels`, vessel),
  ).then(newVessel => newVessel.id);

export const updateVessel = (vessel: UpdatedVessel, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.put<ApiResponse>(`v1.0/vessels/${vessel.id}`, vessel),
  );

export const deleteVessel = (vesselId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.delete<ApiResponse<void>>(`v1.0/vessels/${vesselId}`),
  );

export const fetchVesselCertificatesById = (
  vesselId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<ApiVesselCertificate[]>>(
      `v1.0/vessels/${vesselId}/certificates`,
    ),
  ).then(certificates =>
    certificates.map<VesselCertificate>(certificate => ({
      ...certificate,
      files: certificate.files.map(file => mapFileToAppFile(file)),
    })),
  );

export const addVesselCertificate = (
  vesselId: string,
  certificate: NewVesselCertificate,
  client: AxiosInstance,
) => {
  const formData = new FormData();
  formData.append('classMode', certificate.classMode);
  formData.append('minimumCrew', String(Number(certificate.minimumCrew)));
  formData.append(
    'maximumPassengers',
    String(Number(certificate.maximumPassengers)),
  );
  formData.append('startDate', certificate.startDate);
  formData.append('endDate', certificate.endDate);
  certificate.files.forEach(file => {
    formData.append('files', file.file);
  });

  return handleAxiosApiRequest(
    client.post<ApiResponse<ApiVesselCertificate>>(
      `v1.0/vessels/${vesselId}/certificates`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    ),
  ).then<VesselCertificate>(certificate => ({
    ...certificate,
    files: certificate.files.map(file => mapFileToAppFile(file)),
  }));
};

export const updateVesselCertificate = (
  vesselId: string,
  certificate: NewVesselCertificate & { id: string },
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.put<ApiResponse>(
      `v1.0/vessels/${vesselId}/certificates`,
      certificate,
    ),
  );

export const approveVesselCertificate = (
  vesselId: string,
  certificateId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.put<ApiResponse<VesselCertificate>>(
      `v1.0/vessels/${vesselId}/certificates/${certificateId}/approve`,
    ),
  );

export const rejectVesselCertificate = (
  vesselId: string,
  certificateId: string,
  note: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.put<ApiResponse<VesselCertificate>>(
      `v1.0/vessels/${vesselId}/certificates/${certificateId}/reject`,
      {
        note,
      },
    ),
  );

export const deleteVesselCertificate = (
  vesselId: string,
  certificateId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.delete<ApiResponse>(
      `v1.0/vessels/${vesselId}/certificates/${certificateId}`,
    ),
  );

export const fetchVesselContractAssignments = (
  vesselId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<VesselContractAssignment[]>>(
      `v1.0/vessels/${vesselId}/contracts`,
    ),
  );

export const fetchFile = (client: AxiosInstance, url: string) =>
  client
    .get<File>(url, { responseType: 'blob' })
    .then(response => response.data);

export const fetchCrewMembers = (client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<CrewMemberListItem[]>>(`v1.0/crewmembers`),
  );

export const createCrewMember = (
  crewMember: NewCrewMember,
  client: AxiosInstance,
) => {
  const { crewMemberRole: role, ...restOfCrew } = crewMember;
  return handleAxiosApiRequest(
    client.post<ApiResponse<CrewMember>>(`v1.0/crewmembers`, {
      ...restOfCrew,
      crewRole: role,
    }),
  ).then(newCrewMember => newCrewMember.id);
};

export const updateCrewMember = (
  crewMember: UpdatedCrewMember,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.put<ApiResponse>(`v1.0/crewmembers/${crewMember.id}`, crewMember),
  );

export const deleteCrewMember = (crewMemberId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.delete<ApiResponse<void>>(`v1.0/crewmembers/${crewMemberId}`),
  );

export const fetchCrewMemberById = (
  crewMemberId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<CrewMember>>(`v1.0/crewmembers/${crewMemberId}`),
  );

export const fetchCrewCertificatesById = (
  crewMemberId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<ApiCrewMemberCertificate[]>>(
      `v1.0/crewmembers/${crewMemberId}/certificates`,
    ),
  ).then(certificates =>
    certificates.map<CrewMemberCertificate>(certificate => ({
      ...certificate,
      files: certificate.files.map(file => mapFileToAppFile(file)),
    })),
  );

export const addCrewMemberCertificate = (
  crewMemberId: string,
  certificate: NewCrewMemberCertificate,
  client: AxiosInstance,
) => {
  const formData = new FormData();
  formData.append('type', certificate.type);
  formData.append('startDate', certificate.startDate);
  formData.append('endDate', certificate.endDate);
  certificate.files.forEach(file => {
    formData.append('files', file.file);
  });

  return handleAxiosApiRequest(
    client.post<ApiResponse<ApiCrewMemberCertificate>>(
      `v1.0/crewmembers/${crewMemberId}/certificates`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    ),
  ).then<CrewMemberCertificate>(certificate => ({
    ...certificate,
    files: certificate.files.map(file => mapFileToAppFile(file)),
  }));
};

export const approveCrewMemberCertificate = (
  crewMemberId: string,
  certificateId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.put<ApiResponse<CrewMemberCertificate>>(
      `v1.0/crewmembers/${crewMemberId}/certificates/${certificateId}/approve`,
    ),
  );

export const rejectCrewMemberCertificate = (
  crewMemberId: string,
  certificateId: string,
  note: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.put<ApiResponse<CrewMemberCertificate>>(
      `v1.0/crewmembers/${crewMemberId}/certificates/${certificateId}/reject`,
      { note },
    ),
  );

export const deleteCrewMemberCertificate = (
  crewMemberId: string,
  certificateId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.delete<ApiResponse>(
      `v1.0/crewmembers/${crewMemberId}/certificates/${certificateId}`,
    ),
  );

export const createJourney = (journey: NewJourney, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.post<ApiResponse<Journey>>(`v1.0/journeys`, journey),
  ).then(newJourney => newJourney.id);

export const fetchJourneyById = (journeyId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<Journey>>(`v1.0/journeys/${journeyId}`),
  );

export const rescheduleJourney = (
  journeyChangePayload: JourneyChangePayload,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.post<ApiResponse<JourneyChangeResponse>>(
      `v1.0/journeys/reschedule`,
      journeyChangePayload,
    ),
  );

export const cancelJourney = (
  journeyChangePayload: JourneyChangePayload,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.post<ApiResponse<JourneyChangeResponse>>(
      `v1.0/journeys/cancel`,
      journeyChangePayload,
    ),
  );

export const deleteAdhocJourney = (journeyId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.delete<ApiResponse<void>>(`v1.0/journeys/${journeyId}`),
  );

/**
 * The submit function depends on the related data currently stored.
 * If the operation fails because of the validation, instead of an error,
 * we return a literal "false" value.
 */
export const submitJourney = (
  journeyId: string,
  client: AxiosInstance,
): Promise<false | Journey> =>
  handleAxiosApiRequest(
    client.put<ApiResponse<Journey>>(`v1.0/journeys/${journeyId}/submit`),
  ).catch(error => {
    if (
      error instanceof IotsApiError &&
      error.type === ErrorType.ValidationErrors
    ) {
      return false;
    }
    throw error;
  });

export const unsubmitJourney = (journeyId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.put<ApiResponse<Journey>>(`v1.0/journeys/${journeyId}/unsubmit`),
  );

export const fetchJourneyPlaceholders = (
  startDate: string,
  endDate: string,
  contractId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<JourneyPlaceholder[]>>(`v1.0/journeys/placeholder`, {
      params: {
        startDate,
        endDate,
        contractId,
      },
    }),
  );

export const approveJourney = (journeyId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.put<ApiResponse<Journey>>(`v1.0/journeys/${journeyId}/approve`),
  );

export const rejectJourney = (
  journeyId: string,
  note: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.put<ApiResponse<Journey>>(`v1.0/journeys/${journeyId}/reject`, {
      note,
    }),
  );

export const fetchJourneyLog = (journeyId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<JourneyLog>>(`v1.0/journeys/${journeyId}/log`),
  );

export const updateJourneyLog = (
  updatedJourneyLog: UpdatedJourneyLog,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.post<ApiResponse<JourneyLog>>(
      `v1.0/journeys/log`,
      updatedJourneyLog,
    ),
  );

export const fetchJourneyNotes = (journeyId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<JourneyReasonNote[]>>(
      `v1.0/journeys/${journeyId}/notes`,
    ),
  );

export const fetchInvoices = (client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<InvoiceListItem[]>>(`v1.0/invoices`),
  );

export const validateInvoice = (
  contractId: string,
  year: number,
  month: number,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<InvoiceValidationErrors>>(
      `v1.0/invoices/validate/${contractId}/${year}/${month}`,
    ),
  );

export const fetchInvoiceJourneysByContractId = (
  contractId: string,
  year: number,
  month: number,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<InvoiceJourney[]>>(
      `v1.0/invoices/journeys/${contractId}/${year}/${month}`,
    ),
  );

export const createInvoice = (
  invoice: NewInvoice,
  client: AxiosInstance,
): Promise<CreateInvoiceResponse> => {
  const formData = new FormData();
  formData.append('contractId', invoice.contractId);
  formData.append('month', invoice.month.toString());
  formData.append('year', invoice.year.toString());
  formData.append('invoiceNo', invoice.invoiceNumber);
  invoice.files.forEach(file => {
    formData.append('files', file.file);
  });

  return handleAxiosApiRequest(
    client.post<ApiResponse<CreateInvoiceResponse>>(`v1.0/invoices`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  );
};

export const fetchInvoiceById = (invoiceId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<Invoice>>(`v1.0/invoices/${invoiceId}`),
  );

export const fetchInvoiceJourneysByInvoiceId = (
  invoiceId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<InvoiceJourney[]>>(
      `v1.0/invoices/${invoiceId}/journeys`,
    ),
  );

export const approveInvoice = (invoiceId: string, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.put<ApiResponse<Invoice>>(`v1.0/invoices/${invoiceId}/approve`),
  );

export const rejecInvoice = (
  invoiceId: string,
  note: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.put<ApiResponse<Invoice>>(`v1.0/invoices/${invoiceId}/reject`, {
      note,
    }),
  );

export const fetchInvoiceRejectionNotes = (
  invoiceId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<InvoiceRejectionNote[]>>(
      `v1.0/invoices/${invoiceId}/notes`,
    ),
  );

export const fetchOperatorDashboardInfo = (client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<OperatorDashboardInfo>>(`v1.0/dashboards/operator`),
  );

export const fetchAdminDashboardInfo = (client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<AdminDashboardInfo>>(`v1.0/dashboards/drcd`),
  );

export const fetchAdminDashboardPendingInvoicesInfo = (client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<AdminDashboarPendingInvoiceElement>>(
      `v1.0/dashboards/pendinginvoices`,
    ),
  );

export const fetchYearAnalytics = (year: number, client: AxiosInstance) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<AnalyticsInfo>>(`v1.0/analytics/${year}`),
  );

export const fetchYearAnalyticsCsvFile = (
  year: number,
  client: AxiosInstance,
) =>
  client
    .get<File>(`v1.0/analytics/${year}/csv`, { responseType: 'blob' })
    .then(response => response.data);

export const requestAnalyticsRecalculation = (
  client: AxiosInstance,
): Promise<void> =>
  handleAxiosApiRequest(
    client.post<ApiResponse>(`v1.0/configurations/jobs/AnalyticJob`),
  );

export const fetchVesselInsurancesById = (
  vesselId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<ApiVesselInsurance[]>>(
      `v1.0/vessels/${vesselId}/insurances`,
    ),
  ).then(insurances =>
    insurances.map<VesselInsurance>(insurance => ({
      ...insurance,
      files: insurance.documents.map(file => mapFileToAppFile(file)),
    })),
  );

export const addVesselInsurance = (
  vesselId: string,
  insurance: NewVesselInsurance,
  client: AxiosInstance,
) => {
  const formData = new FormData();
  formData.append('coverageType', insurance.coverageType);
  formData.append('startDate', insurance.startDate);
  formData.append('endDate', insurance.endDate);
  insurance.files.forEach(file => {
    formData.append('files', file.file);
  });

  return handleAxiosApiRequest(
    client.post<ApiResponse<ApiVesselInsurance>>(
      `v1.0/vessels/${vesselId}/insurances`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    ),
  ).then<VesselInsurance>(insurance => ({
    ...insurance,
    files: insurance.documents.map(file => mapFileToAppFile(file)),
  }));
};

export const deleteVesselInsurance = (
  vesselId: string,
  insuranceId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.delete<ApiResponse>(
      `v1.0/vessels/${vesselId}/insurances/${insuranceId}`,
    ),
  );

export const fetchContractInsurancesById = (
  contractId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.get<ApiResponse<ApiContractInsurance[]>>(
      `v1.0/contracts/${contractId}/insurances`,
    ),
  ).then(insurances =>
    insurances.map<ContractInsurance>(insurance => ({
      ...insurance,
      files: insurance.documents.map(file => mapFileToAppFile(file)),
    })),
  );

export const addContractInsurance = (
  contractId: string,
  insurance: NewContractInsurance,
  client: AxiosInstance,
) => {
  const formData = new FormData();
  formData.append('coverageType', insurance.coverageType);
  formData.append('startDate', insurance.startDate);
  formData.append('endDate', insurance.endDate);
  insurance.files.forEach(file => {
    formData.append('files', file.file);
  });

  return handleAxiosApiRequest(
    client.post<ApiResponse<ApiContractInsurance>>(
      `v1.0/contracts/${contractId}/insurances`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    ),
  ).then<ContractInsurance>(insurance => ({
    ...insurance,
    files: insurance.documents.map(file => mapFileToAppFile(file)),
  }));
};

export const deleteContractInsurance = (
  contractId: string,
  insuranceId: string,
  client: AxiosInstance,
) =>
  handleAxiosApiRequest(
    client.delete<ApiResponse>(
      `v1.0/contracts/${contractId}/insurances/${insuranceId}`,
    ),
  );
